import { VideoItem } from '../types';

const SEARCH_ENGINE_URL = 'https://connected-league-search-engine-wnnomr6jma-ew.a.run.app';

/** Clips asked for per highlight search; only the free ones are kept, so fewer are shown. */
const HIGHLIGHTS_PER_PAGE = 50;

/** Match event types as shown in the apps. Types without one are shown as they are. */
const MATCH_EVENT_TYPE_TEXT: Record<string, string> = {
  GOAL: 'Mål',
  SHOT: 'Avslut',
  SHOT_ON_TARGET: 'Avslut på mål',
  WARNING: 'Gult kort',
  PENALTY: 'Rött kort',
  PENALTY_KICK: 'Straff',
  PENALTY_MISSED: 'Straffmiss',
  FREE_KICK: 'Frispark',
  CORNER: 'Hörna',
  OFFSIDE: 'Offside',
  SUBSTITUTION: 'Byte',
};

interface SearchVideosOptions {
  channels?: string[];
  page?: number;
}

export async function searchVideos(
  query: string,
  { channels = [], page = 0 }: SearchVideosOptions = {}
): Promise<VideoItem[]> {
  if (channels.length === 0) {
    return [];
  }

  const encodedQuery = encodeURIComponent(query);
  const params = new URLSearchParams({ search: encodedQuery });

  params.append('channels', channels.join(','));

  const url = `${SEARCH_ENGINE_URL}/search/fotbollplay-videos/${page}?${params.toString()}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error('Failed to search videos');
  }

  const data = await response.json();

  return data.data.map((video: Record<string, unknown>) => ({
    id: video.id as string,
    title: video.description as string,
    thumbnail: (video.thumbnail || video.thumbnailUrl || '') as string,
    duration: video.durationAsText as string,
    durationSeconds: (video.durationInSeconds as number) || 0,
    channelName: video.channelName as string | undefined,
    category: video.category as string | undefined,
    ...video,
  }));
}

interface SearchHighlightsOptions {
  /** The app's league, e.g. `allsvenskan` (`configLeagueName` in its app config). */
  league?: string;
  teamAbbrv?: string;
  page?: number;
}

/**
 * Searches the match event clips of the team's own events (`byTeam`) in its league, newest
 * season first. Only clips free to watch are kept. A clip keeps every search field, with
 * `source: 'matchEvent'` and its length in `durationInMs`, for GraphQL to shape it later.
 */
export async function searchHighlights(
  query: string,
  { league, teamAbbrv, page = 1 }: SearchHighlightsOptions = {}
): Promise<VideoItem[]> {
  if (!league || !teamAbbrv) {
    return [];
  }

  const params = new URLSearchParams({
    search: query,
    team: teamAbbrv,
    perPage: String(HIGHLIGHTS_PER_PAGE),
  });

  const url = `${SEARCH_ENGINE_URL}/search/matchevents/${encodeURIComponent(league)}/${page}?${params.toString()}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error('Failed to search highlights');
  }

  const data = await response.json();

  return (data.data as Record<string, unknown>[])
    .filter((clip) => clip.isAvailablePublicly === true)
    .map((clip) => {
      const durationInMs = Number(clip.duration) || 0;
      const type = clip.type as string;
      const homeTeam = (clip.homeTeamName || clip.homeTeamAbbrv) as string;
      const visitingTeam = (clip.visitingTeamName || clip.visitingTeamAbbrv) as string;

      return {
        ...clip,
        id: clip.id as string,
        source: 'matchEvent' as const,
        title: clip.description as string,
        thumbnail: (clip.thumbnailUrl || clip.thumbnail || '') as string,
        duration: formatTime(durationInMs / 1000),
        durationInMs,
        durationSeconds: Math.round(durationInMs / 1000),
        date: clip.matchStartDate,
        channelName: `${homeTeam}–${visitingTeam}`,
        category:
          `${MATCH_EVENT_TYPE_TEXT[type] ?? type} ${clip.minuteWithStoppageTime ?? ''}`.trim(),
      };
    });
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Parse duration string like "2:30", "1:05:30", or "45" to seconds */
export function parseDurationString(durationStr: string | undefined): number | null {
  if (!durationStr || typeof durationStr !== 'string') return null;
  const parts = durationStr
    .trim()
    .split(':')
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => isNaN(n))) return null;
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

export function getVideoDuration(video: VideoItem): number {
  const fromNumber = video['durationSeconds'] ?? video['durationInSeconds'];
  if (typeof fromNumber === 'number' && fromNumber > 0) return fromNumber;
  const fromString = parseDurationString(
    (video.duration ?? video['durationAsText']) as string | undefined
  );
  if (fromString !== null && fromString > 0) return fromString;
  return 60;
}

export function getVideoUrl(video: VideoItem): string {
  const direct =
    (video['url'] as string) ||
    (video['videoUrl'] as string) ||
    (video['streamUrl'] as string) ||
    '';
  const hls = (video['hlsUrl'] as string) || '';
  return direct || hls || '';
}

export function isHlsUrl(url: string): boolean {
  return !!url && (url.includes('.m3u8') || url.includes('m3u8'));
}
